import React, { useEffect, useState } from 'react';
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableView from "./util/TableView";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IconButton from "@mui/material/IconButton";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { api } from "../api/api";
import SecretSantaAddCustomPresentDialog from "./dialog/SecretSantaAddCustomPresentDialog";
import Tooltip from "@mui/material/Tooltip";
import { transcribeSantaDeliveryStatus } from "../api/helper";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import { Select, MenuItem, FormControl, Menu, ListItemText } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { preprocessLocations } from "./util/SecretSantaLocationUtil";
import { SantaPresentDeliveryStatuses } from "../api/models";
import Spoiler from "./component/Spoiler";
import { deliveryColors } from "./component/secretSanta/common/SantasStatusCounts";
import { GrinchIcon, GrinchCrossedIcon } from "../components/icons/GrinchIcon";
import ListItemIcon from "@mui/material/ListItemIcon";
import GrinchCommentDialog from "./dialog/GrinchCommentDialog";

export default function SecretSantasView(props) {
  const [data, setData] = useState([]);

  const [openAddSantaDialog, setOpenAddSantaDialog] = useState(false);
  const [addSantaApproval, setAddSantaApproval] = useState(false);

  const [openGrinchCommentDialog, setOpenGrinchCommentDialog] = useState(false);
  const [grinchActiveStatusIntent, setGrinchActiveStatusIntent] = useState(true);
  const [grinchReceiver, setGrinchReceiver] = useState(null);
  const [grinchEditIndex, setGrinchEditIndex] = useState(null);

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [locations, setLocations] = useState([])
  const [filter, setFilter] = useState({})

  const [contextMenu, setContextMenu] = useState(null);

  useEffect(() => {
    props.setShowSearch(true);

    (async () => {
      await api.secretSanta.getLocations()
        .then(santaLocations => {
          setLocations(santaLocations)
        })
        .catch((error) => props.onFetchFailed(error));
    })();
  }, []);

  useEffect(() => {
    setFilter(
      {
        santaName: props.searchValue,
        locationName: selectedLocation,
        statusName: selectedStatus
      }
    )
  }, [props.searchValue, selectedLocation, selectedStatus]);


  useEffect(() => {
    if (addSantaApproval) {
      setOpenAddSantaDialog(false)
      setAddSantaApproval(false)
    }
  }, [addSantaApproval]);

  const handleOnChangeDeliveryStatus = (receiverId, index) => {
    (async () => {
      api.secretSanta.setNextDeliveryStatus(receiverId)
        .then(santa => {
          let newArr = data.slice(0, data.length);
          newArr[index] = santa;
          setData(newArr);
        })
        .catch((error) => {
          props.onFetchFailed(error);
        })
    })();
  };

  const handleOnPresentsCountChange = (receiverId, index, presentsCount) => {
    if (presentsCount < 1) {
      presentsCount = 1
    }
    if (data[index].presentsCount !== presentsCount) {
      (async () => {
        api.secretSanta.setPresentsCount(receiverId, presentsCount)
          .then(santa => {
            let newArr = data.slice(0, data.length);
            newArr[index] = santa;
            setData(newArr);
          })
          .catch((error) => {
            props.onFetchFailed(error);
          });
      })();
    }
  };

  const handleContextMenu = (event, receiver, index) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 4,
          receiver,
          index
        }
        : null,
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleToggleGrinch = (activateAction) => {
    if (contextMenu) {
      setGrinchActiveStatusIntent(activateAction);
      setGrinchReceiver(contextMenu?.receiver);
      setGrinchEditIndex(contextMenu?.index);
      setOpenGrinchCommentDialog(true);
    }
    handleCloseContextMenu();
  };

  const handleGrinchUpdateSuccess = (grinch) => {
    console.log("Updating grinch at index:", grinchEditIndex);
    let newArr = data.slice(0, data.length);
    newArr[grinchEditIndex].isGrinch = grinch.isActive;
    newArr[grinchEditIndex].grinchComment = grinch.comment;
    setData(newArr);
  };

  const handleOnFilterClear = () => {
    setSelectedLocation(null);
    setSelectedStatus(null);
  }


  const tableHeader =
    <TableRow>
      <TableCell>Status</TableCell>
      <TableCell>Receiver's Name</TableCell>
      <TableCell style={{ width: 50 }}>Presents count</TableCell>
      <TableCell>Santa's Name</TableCell>
      <TableCell>Receiver's Wishes</TableCell>
      <TableCell>Location</TableCell>
      <TableCell>Delivery</TableCell>
    </TableRow>;

  const tableRows = (receiver, index) => {
    let icon = receiver.deliveryStatus === "NOT_DELIVERED"
      ? <CheckBoxOutlineBlankIcon />
      : < CheckBoxIcon sx={{ color: deliveryColors[receiver.deliveryStatus] }} />

    return <TableRow key={receiver.id}
                     onContextMenu={(event) => handleContextMenu(event, receiver, index)}
    >
      <TableCell>{
        <Tooltip title={transcribeSantaDeliveryStatus(receiver.deliveryStatus)}>
          <IconButton
            onClick={() => handleOnChangeDeliveryStatus(receiver.id, index)}
          >
            {icon}
          </IconButton>
        </Tooltip>
      }
      </TableCell>
      <TableCell>
        <span>{receiver?.receiverName}</span>
        {receiver?.isGrinch &&
          <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{receiver?.grinchComment}</div>} placement="top">
            <span><GrinchIcon /></span>
          </Tooltip>
        }
      </TableCell>
      <TableCell style={{ width: 50 }}>
        <TextField type="number"
                   variant="standard"
                   value={receiver.presentCount ?? ''}
                   onChange={(e) => handleOnPresentsCountChange(receiver.id, index, e.target.value)}
        />
      </TableCell>
      <TableCell>
        <Spoiler>{receiver?.santaName ?? ''}</Spoiler>
      </TableCell>
      <TableCell>{receiver?.wishes}</TableCell>
      <TableCell>{receiver?.locationName}</TableCell>
      <TableCell>
        {receiver?.lockersSupported === false
          ? '🎄'
          : `📦: ${receiver?.lockerLocation ? receiver?.lockerLocation + '> Cell:' + receiver?.lockerCell : '🤷‍♂️'}`
        }
      </TableCell>

    </TableRow>
  };

  return (
    <Paper
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          marginBottom: 20
        }}
      >

        <FormControl
          variant="standard"
          sx={{
            marginLeft: 2,
            marginRight: 2
          }}
        >
          <Select
            sx={{ width: 200 }}
            labelId="select-location-input"
            value={selectedLocation || ""}
            onChange={(event) => {
              setSelectedLocation(event.target.value);
            }}
            displayEmpty
          >
            <MenuItem value="">
              <em>All locations</em>
            </MenuItem>
            {preprocessLocations(locations)
              .map((location) => (
                <MenuItem key={location.id} value={location.name}>
                  {location.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl
          variant="standard"
          sx={{
            marginLeft: 2,
            marginRight: 2
          }}
        >
          <Select
            sx={{ width: 200 }}
            labelId="select-status-input"
            value={selectedStatus || ""}
            onChange={(event) => {
              setSelectedStatus(event.target.value);
            }}
            displayEmpty
          >
            <MenuItem value="">
              <em>All statuses</em>
            </MenuItem>
            {SantaPresentDeliveryStatuses
              .map((status) => (
                <MenuItem key={status} value={status}>
                  {transcribeSantaDeliveryStatus(status)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <IconButton
          onClick={handleOnFilterClear}
          aria-label="clear">
          <ClearIcon />
        </IconButton>
      </div>
      <SecretSantaAddCustomPresentDialog
        component={'span'}
        open={openAddSantaDialog}
        handleClose={() => setOpenAddSantaDialog(false)}
        handleApprove={() => setAddSantaApproval(true)}
        onFetchFailed={props.onFetchFailed}
      />
      <GrinchCommentDialog
        open={openGrinchCommentDialog}
        handleClose={() => setOpenGrinchCommentDialog(false)}
        onSuccess={handleGrinchUpdateSuccess}
        onFetchFailed={props.onFetchFailed}
        receiver={grinchReceiver}
        activateAction={grinchActiveStatusIntent}
      />
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => handleToggleGrinch(true)}>
          <ListItemIcon>
            <GrinchIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {contextMenu?.receiver?.isGrinch ? 'Edit grinch comment' : 'Make grinch'}
          </ListItemText>
        </MenuItem>
        {contextMenu?.receiver?.isGrinch &&
          <MenuItem onClick={() => handleToggleGrinch(false)}>
            <ListItemIcon>
              <GrinchCrossedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              Unmake grinch
            </ListItemText>
          </MenuItem>
        }
      </Menu>
      <Fab color="primary" aria-label="add" variant="extended"
           sx={{
             margin: 0,
             top: 'auto',
             right: 20,
             bottom: 20,
             left: 'auto',
             position: 'fixed'
           }}
           disabled={true}
           onClick={() => setOpenAddSantaDialog(true)}
      >
        <AddIcon sx={{ mr: 1 }} />
        Add Custom Presents
      </Fab>
      <TableView
        fetchUrl={"secretsanta/all"}
        tableHeader={tableHeader}
        tableRows={tableRows}
        searchValue={props.searchValue}
        data={data}
        setData={setData}
        filter={filter}
        methodType={'POST'}
        defaultPageSize={100}
        {...props}
      />
    </Paper>
  );
}
